<template>
  <NavBar v-if="userLoaded" pageName="Distributor Service Products"></NavBar>

  <v-container style="margin-top: 185px;">
    <!-- Loading Overlay -->
    <v-overlay :model-value="loading" class="align-center justify-center">
      <v-progress-circular indeterminate size="64"></v-progress-circular>
    </v-overlay>

    <v-btn variant="text" size="small" @click="$router.push('/settings/services')" class="mb-2">
      <v-icon size="small" class="mr-1">mdi-arrow-left</v-icon>
      Services Settings
    </v-btn>

    <!-- Header with actions -->
    <v-row class="align-center mb-4">
      <v-col cols="12" md="6">
        <h2>Distributor Service Products</h2>
        <p class="text-caption text-grey">Map services to billing products for each distributor (used in MVNO API calls)</p>
      </v-col>
      <v-col cols="12" md="6" class="d-flex justify-end gap-2">
        <v-btn color="primary" variant="outlined" :loading="loading" @click="loadData">
          <v-icon class="mr-2">mdi-refresh</v-icon>
          Refresh
        </v-btn>
        <v-btn color="success" @click="openAddDialog">
          <v-icon class="mr-2">mdi-plus</v-icon>
          Add Mapping
        </v-btn>
      </v-col>
    </v-row>

    <!-- Mappings Table -->
    <v-card>
      <v-card-title>Service-Product Mappings: {{ mappings.length }}</v-card-title>
      <v-card-text>
        <v-data-table
          :headers="tableHeaders"
          :items="mappings"
          :loading="loading"
          :items-per-page="20"
          item-value="id"
          class="elevation-1"
        >
          <!-- Service column -->
          <template #[`item.service_name`]="{ item }">
            <div>
              <strong>{{ item.service_name }}</strong>
              <div class="text-caption text-grey">ID: {{ item.service_id }}</div>
            </div>
          </template>

          <!-- Distributor column -->
          <template #[`item.distributor_name`]="{ item }">
            <div>
              <strong>{{ item.distributor_name }}</strong>
              <div class="text-caption text-grey">ID: {{ item.distributor_id }}</div>
            </div>
          </template>

          <!-- Product column -->
          <template #[`item.product_name`]="{ item }">
            <div>
              <strong>{{ item.product_name }}</strong>
              <div class="text-caption text-grey">
                <v-chip size="x-small" color="primary">i_product: {{ item.i_product || 'N/A' }}</v-chip>
              </div>
            </div>
          </template>

          <!-- MVNO ID column -->
          <template #[`item.mvno_id`]="{ item }">
            <v-chip v-if="item.mvno_id" size="small" color="info" variant="outlined">
              {{ item.mvno_id }}
            </v-chip>
            <span v-else class="text-grey text-caption">All</span>
          </template>

          <!-- Active indicator -->
          <template #[`item.active`]="{ item }">
            <v-chip
              :color="item.active ? 'success' : 'error'"
              size="small"
              @click="toggleActive(item)"
              style="cursor: pointer;"
            >
              {{ item.active ? 'Active' : 'Inactive' }}
            </v-chip>
          </template>

          <!-- Actions column -->
          <template #[`item.actions`]="{ item }">
            <v-btn
              size="x-small"
              color="primary"
              class="mr-1"
              @click="openEditDialog(item)"
              title="Edit mapping"
            >
              <v-icon>mdi-pencil</v-icon>
            </v-btn>
            <v-btn
              size="x-small"
              color="error"
              @click="confirmDelete(item)"
              title="Delete mapping"
            >
              <v-icon>mdi-delete</v-icon>
            </v-btn>
          </template>

          <!-- No data -->
          <template v-slot:no-data>
            <v-alert type="info" class="ma-2">
              No service-product mappings found
            </v-alert>
          </template>
        </v-data-table>
      </v-card-text>
    </v-card>

    <!-- Info alert -->
    <v-alert type="info" density="compact" class="mt-4">
      <v-icon class="mr-2">mdi-information</v-icon>
      These mappings define which <strong>billing product (i_product)</strong> to use when calling the MVNO API for a specific service and distributor.
      The system traverses the client hierarchy to find the appropriate product mapping.
    </v-alert>

    <!-- Add/Edit Dialog (subcomponent) -->
    <DistributorServiceProductDialog
      :show="formDialog"
      :is-editing="isEditing"
      :form-data="formData"
      :distributors="distributors"
      :token="token"
      :saving="saving"
      @close="formDialog = false"
      @save="saveMapping"
      @show-snackbar="handleSnackbar"
    />

    <!-- Delete Confirmation Dialog -->
    <v-dialog v-model="deleteDialog" max-width="400">
      <v-card>
        <v-card-title class="bg-error text-white">
          <v-icon class="mr-2">mdi-delete</v-icon>
          Confirm Delete
        </v-card-title>
        <v-card-text class="pt-4">
          Are you sure you want to delete this mapping?
          <div class="mt-2" v-if="itemToDelete">
            <strong>Service:</strong> {{ itemToDelete.service_name }}<br>
            <strong>Distributor:</strong> {{ itemToDelete.distributor_name }}<br>
            <strong>Product:</strong> {{ itemToDelete.product_name }} (i_product: {{ itemToDelete.i_product }})
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn @click="deleteDialog = false">Cancel</v-btn>
          <v-btn color="error" @click="deleteMapping" :loading="deleting">Delete</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Snackbar for notifications -->
    <v-snackbar v-model="snackbar.show" :color="snackbar.color" :timeout="3000">
      {{ snackbar.message }}
    </v-snackbar>
  </v-container>
</template>

<script>
import { defineComponent } from 'vue';
import NavBar from "@/components/NavBar.vue";
import DistributorServiceProductDialog from "@/components/dialogs/DistributorServiceProductDialog.vue";
import useGetLoggedIn from "@/composables/useGetLoggedIn";
import {
  getDistributorServiceProducts,
  addDistributorServiceProduct,
  updateDistributorServiceProduct,
  deleteDistributorServiceProduct,
  getDistributorsForSelect
} from "@/composables/useDistributorServiceProductsAPI";
import { log } from "@/composables/useLog";
import store from "@/store";

export default defineComponent({
  name: 'DistributorServiceProductsSettings',
  components: { NavBar, DistributorServiceProductDialog },
  data() {
    return {
      loading: false,
      token: '',
      userLoaded: false,
      mappings: [],
      distributors: [],

      // Dialog
      formDialog: false,
      isEditing: false,
      saving: false,
      formData: {
        id: null,
        service_id: null,
        distributor_id: null,
        product_id: null,
        mvno_id: null,
        active: true
      },

      // Delete dialog
      deleteDialog: false,
      deleting: false,
      itemToDelete: null,

      // Snackbar
      snackbar: {
        show: false,
        message: '',
        color: 'success'
      },

      // Table headers
      tableHeaders: [
        { title: 'ID', key: 'id', width: '5%' },
        { title: 'Distributor', key: 'distributor_name', width: '16%' },
        { title: 'Service', key: 'service_name', width: '18%' },
        { title: 'Service Type', key: 'service_type', width: '12%' },
        { title: 'Billing Product', key: 'product_name', width: '18%' },
        { title: 'MVNO', key: 'mvno_id', align: 'center', width: '6%' },
        { title: 'Status', key: 'active', align: 'center', width: '8%' },
        { title: 'Actions', key: 'actions', sortable: false, width: '10%' }
      ]
    }
  },
  methods: {
    showSnackbar(message, color = 'success') {
      this.snackbar.message = message;
      this.snackbar.color = color;
      this.snackbar.show = true;
    },

    handleSnackbar(payload) {
      this.showSnackbar(payload.message, payload.color);
    },

    async loadData() {
      this.loading = true;
      try {
        const [mappingsResult, distributorsResult] = await Promise.all([
          getDistributorServiceProducts(this.token),
          getDistributorsForSelect(this.token)
        ]);

        log("Mappings loaded:", mappingsResult);

        if (!mappingsResult.error) {
          this.mappings = mappingsResult.distributor_service_products || [];
        } else {
          this.showSnackbar(mappingsResult.message || 'Error loading mappings', 'error');
        }

        if (!distributorsResult.error) {
          this.distributors = distributorsResult.distributors || [];
        }
      } catch (err) {
        log("Error loading data:", err);
        this.showSnackbar('Error loading data', 'error');
      } finally {
        this.loading = false;
      }
    },

    openAddDialog() {
      this.isEditing = false;
      this.formData = {
        id: null,
        service_id: null,
        distributor_id: null,
        product_id: null,
        mvno_id: null,
        active: true
      };
      this.formDialog = true;
    },

    openEditDialog(item) {
      this.isEditing = true;
      this.formData = {
        id: item.id,
        service_id: item.service_id,
        distributor_id: item.distributor_id,
        product_id: item.product_id,
        mvno_id: item.mvno_id,
        active: item.active
      };
      this.formDialog = true;
    },

    async saveMapping(data) {
      this.saving = true;
      try {
        let result;
        if (this.isEditing) {
          result = await updateDistributorServiceProduct(this.token, data);
        } else {
          result = await addDistributorServiceProduct(this.token, data);
        }

        if (!result.error) {
          this.showSnackbar(this.isEditing ? 'Mapping updated successfully' : 'Mapping added successfully');
          this.formDialog = false;
          await this.loadData();
        } else {
          this.showSnackbar(result.message || 'Error saving mapping', 'error');
        }
      } catch (err) {
        log("Error saving mapping:", err);
        this.showSnackbar('Error saving mapping', 'error');
      } finally {
        this.saving = false;
      }
    },

    async toggleActive(item) {
      this.loading = true;
      try {
        const result = await updateDistributorServiceProduct(this.token, {
          id: item.id,
          active: !item.active
        });

        if (!result.error) {
          this.showSnackbar(`Mapping ${!item.active ? 'activated' : 'deactivated'} successfully`);
          await this.loadData();
        } else {
          this.showSnackbar(result.message || 'Error updating mapping', 'error');
        }
      } catch (err) {
        log("Error toggling active status:", err);
        this.showSnackbar('Error updating mapping', 'error');
      } finally {
        this.loading = false;
      }
    },

    confirmDelete(item) {
      this.itemToDelete = item;
      this.deleteDialog = true;
    },

    async deleteMapping() {
      if (!this.itemToDelete) return;

      this.deleting = true;
      try {
        const result = await deleteDistributorServiceProduct(this.token, this.itemToDelete.id);

        if (!result.error) {
          this.showSnackbar('Mapping deleted successfully');
          this.deleteDialog = false;
          this.itemToDelete = null;
          await this.loadData();
        } else {
          this.showSnackbar(result.message || 'Error deleting mapping', 'error');
        }
      } catch (err) {
        log("Error deleting mapping:", err);
        this.showSnackbar('Error deleting mapping', 'error');
      } finally {
        this.deleting = false;
      }
    }
  },

  async created() {
    const { getLoggedIn } = useGetLoggedIn();
    await getLoggedIn();
    this.token = store.getters.getToken;
    this.userLoaded = true;
    await this.loadData();
  }
});
</script>

<style scoped>
.gap-2 {
  gap: 8px;
}
</style>
